import { sampleSource, levels, loadImage, blur, sobel, tint } from "../shared";

class Scan {
  constructor(ctx) { this.ctx = ctx; this.frame = 0; }
  get writesColors() { return true; }
  resize() { this.resample(); }
  resample() { const src = this.video || this.img; if (src) this.data = sampleSource(src, this.ctx.N, this.ctx.params.mirror && !!this.video); }
  async startCam() {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error("Webcam not available in this browser");
    this.stopCam();
    try { this.stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } }); }
    catch { throw new Error("Camera permission denied or no camera found"); }
    const v = document.createElement("video"); v.srcObject = this.stream; v.muted = true; v.playsInline = true; await v.play(); this.video = v;
  }
  stopCam() { this.stream?.getTracks().forEach((t) => t.stop()); this.stream = null; this.video = null; }
  step(dt, t) {
    const P = this.ctx.params, { N, target, colors } = this.ctx;
    if (this.video && !P.freeze && this.frame++ % 2 === 0) this.resample();
    if (!this.data) {
      const scan = ((t * 0.35) % 1) * N;
      for (let i = 0; i < target.length; i++) { const r = (i / N) | 0, d = Math.abs(r - scan); target[i] = d < 2 ? 0.2 : -0.8; tint(colors, i, d < 2 ? 1 : 0.1, 0.9); }
      return;
    }
    let l = this.data.lum; if (P.blur > 0) l = blur(l, N, P.blur); if (P.edges) l = sobel(l, N);
    const rgb = this.data.rgb, D = P.depth * this.ctx.g.force;
    for (let i = 0; i < l.length; i++) {
      const v = levels(l[i], P.black, P.white, P.gamma, P.invert); target[i] = Math.max(-1, Math.min(1, (2 * v - 1) * D));
      if (P.trueColor && !P.edges) { colors[i * 3] = 0.15 + rgb[i * 3] * 0.95; colors[i * 3 + 1] = 0.15 + rgb[i * 3 + 1] * 0.95; colors[i * 3 + 2] = 0.15 + rgb[i * 3 + 2] * 0.95; }
      else tint(colors, i, v, 0.8);
    }
  }
  onParams(key) { if (key === "mirror") this.resample(); }
  async action(id, file) {
    if (id === "upload") { this.stopCam(); this.img = await loadImage(file); this.resample(); return "Image scanned"; }
    if (id === "cam") { await this.startCam(); return "Webcam scanning live"; }
    if (id === "stopCam") { this.stopCam(); this.resample(); }
  }
  clear() { this.stopCam(); this.img = null; this.data = null; }
  telemetry() {
    const P = this.ctx.params;
    return [["Source", this.video ? "WEBCAM" : this.img ? "IMAGE" : "AWAITING"], ["Status", this.video && !P.freeze ? "LIVE" : this.data ? "HELD" : "SCANNING"], ["Mapping", P.edges ? "EDGES" : P.invert ? "DARK ↑" : "LIGHT ↑"], ["Grid", `${this.ctx.N}²`]];
  }
  dispose() { this.stopCam(); }
}

const scan = {
  id: "scan-lab", name: "Scan Lab", icon: "ScanFace", rest: -0.8,
  desc: "Turn photos or a live webcam feed into a 3D relief. Brightness is treated as depth (browsers do not expose true camera depth, so luminance is used as a depth estimate); tune the levels to choose what protrudes and what recesses.",
  math: "h = (2·((L − b)/(w − b))^γ − 1) · D",
  params: [
    { key: "depth", label: "Relief depth", type: "range", min: 0.1, max: 1, step: 0.01, def: 0.9 },
    { key: "invert", label: "Dark areas protrude", type: "toggle", def: false },
    { key: "black", label: "Dark point", type: "range", min: 0, max: 0.9, step: 0.01, def: 0.05 },
    { key: "white", label: "Light point", type: "range", min: 0.1, max: 1, step: 0.01, def: 0.95 },
    { key: "gamma", label: "Contrast curve", type: "range", min: 0.3, max: 3, step: 0.05, def: 1 },
    { key: "blur", label: "Soften", type: "range", min: 0, max: 4, step: 1, def: 1 },
    { key: "edges", label: "Edge relief (Sobel)", type: "toggle", def: false },
    { key: "trueColor", label: "Show image colour", type: "toggle", def: true },
    { key: "mirror", label: "Mirror webcam", type: "toggle", def: true },
    { key: "freeze", label: "Freeze frame", type: "toggle", def: false },
  ],
  actions: [{ id: "upload", label: "Upload image", kind: "file", testid: "scan-upload-input" }, { id: "cam", label: "Start webcam", testid: "scan-start-webcam" }, { id: "stopCam", label: "Stop webcam", testid: "scan-stop-webcam" }],
  create: (ctx) => new Scan(ctx),
};
export default scan;
